import { ReebeloStoreT } from '@lambda/reebelo';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  Method,
} from '@lambda/commons/apis/axios';
import { Logger } from '@lambda/commons/logging/logger';
import SSM from '@lambda/commons/apis/ssm';
import logAxios from '@lambda/commons/logAxios';
import {
  CustomerAddressType,
  CustomerSubscriptionChannel,
  CustomerSubscriptionStatus,
  UserRoute,
} from './enum';
import {
  CreateCustomer,
  Customer,
  CustomerAddress,
  CustomerAlias,
  CustomerCheckout,
  CustomerSearchResults,
  CustomerVerification,
  VerifyCustomer,
} from './types';

export default class CustomerApi {
  private api: AxiosInstance;

  protected store: ReebeloStoreT;

  protected logger: Logger;

  private ssm: SSM;

  private url = '';

  private apiKey = '';

  constructor(store: ReebeloStoreT) {
    this.store = store;
    this.logger = new Logger('ms-customer');
    this.ssm = new SSM();
    this.api = axios.create({
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    logAxios(this.api, this.logger);
  }

  async forgotPassword(email: string): Promise<void> {
    await this.makeApiRequest<void>(
      'POST',
      UserRoute.FORGOT,
      {},
      {
        email,
      },
    );
  }

  async resetPassword(
    shopifyCustomerId: number,
    resetToken: string,
    password: string,
  ): Promise<Customer> {
    return this.makeApiRequest<Customer>(
      'POST',
      UserRoute.RESET,
      {},
      {
        customerId: shopifyCustomerId,
        resetToken,
        password,
      },
    );
  }

  async loginCustomer(email: string, password: string): Promise<Customer> {
    const auth = await this.makeApiRequest<Customer>(
      'POST',
      UserRoute.LOGIN,
      {},
      {
        email,
        password,
      },
    );

    if (!auth) throw new Error('Invalid email or password');

    return auth;
  }

  async loginCustomerWithMultipass(email: string): Promise<Customer> {
    const auth = await this.makeApiRequest<Customer>(
      'POST',
      UserRoute.MULTIPASS,
      {},
      {
        email,
      },
    );

    if (!auth) throw new Error('Invalid email or password');

    return auth;
  }

  async logoutCustomer(shopifyToken: string): Promise<void> {
    if (!shopifyToken) return;

    await this.makeApiRequest<void>(
      'DELETE',
      UserRoute.LOGOUT,
      {},
      {
        access_token: shopifyToken,
      },
    );
  }

  async createCustomer(body: CreateCustomer): Promise<Customer> {
    this.logger.info({ store: this.store }, 'creating customer');

    const auth = await this.makeApiRequest<Customer>(
      'POST',
      UserRoute.USER,
      {},
      body,
    );

    if (!auth) throw new Error('Error creating customer');

    return auth;
  }

  async getCurrentCustomer(shopifyToken: string): Promise<Customer | null> {
    const customer = await this.makeApiRequest<Customer>(
      'GET',
      UserRoute.USER,
      {
        'X-Reebelo-Shop-Token': shopifyToken,
      },
    );

    this.logger.debug({ customer }, 'customer result');

    return customer;
  }

  async getCustomerById(reebeloId: string): Promise<Customer | null> {
    try {
      this.logger.info(
        { store: this.store, url: this.url, reebeloId },
        'getting customer by id',
      );
      if (!reebeloId) this.logger.warn('Missing customer id');

      const route = UserRoute.CUSTOMER.replace(
        '{customerId}',
        reebeloId,
      ) as UserRoute;

      const customer = await this.makeApiRequest<Customer>('GET', route);

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          reebeloId: customer?.reebeloId,
          shopifyId: customer?.shopifyId,
        },
        'found customer by id',
      );

      return customer;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error getting customer by id');
      this.logger.debug(error, 'error getting customer by id');

      return null;
    }
  }

  async getCustomerByEmail(email: string): Promise<Customer | null> {
    try {
      this.logger.info(
        { store: this.store, url: this.url },
        'getting customer by email',
      );

      const response = await this.makeApiRequest<CustomerSearchResults>(
        'GET',
        UserRoute.SEARCH,
        {},
        {},
        { email },
      );

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          response,
        },
        'found customers by email',
      );

      if (!response) return null;

      return response.customers[0] || null;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error getting customer by email');
      this.logger.debug(error, 'error getting customer by email');

      return null;
    }
  }

  async getOrCreateCustomerByEmail(email: string): Promise<Customer> {
    const customer = await this.getCustomerByEmail(email);

    if (customer) {
      this.logger.info(
        {
          store: this.store,
          url: this.url,
          customer,
        },
        'found existing customer by email',
      );

      return customer;
    }

    this.logger.info(
      { store: this.store, url: this.url, email },
      'creating customer by email',
    );

    return this.createCustomer({ email, firstName: '', lastName: '' });
  }

  async createCheckoutUrl(shopifyToken: string, redirectUrl: string) {
    this.logger.info({ redirectUrl }, 'creating customer checkout url');

    return this.makeApiRequest<CustomerCheckout>(
      'POST',
      UserRoute.CHECKOUT,
      {
        'X-Reebelo-Shop-Token': shopifyToken,
      },
      {
        originalCheckoutUrl: redirectUrl,
      },
    );
  }

  async updateCustomer(customer: Customer): Promise<Customer> {
    try {
      this.logger.info(
        { store: this.store, url: this.url },
        'updating customer',
      );

      const route = UserRoute.CUSTOMER.replace(
        '{customerId}',
        customer.reebeloId,
      ) as UserRoute;

      const updatedCustomer = await this.makeApiRequest<Customer>(
        'PUT',
        route,
        {},
        customer,
      );

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          reebeloId: updatedCustomer?.reebeloId,
          shopifyId: updatedCustomer?.shopifyId,
        },
        'updated customer',
      );

      return updatedCustomer;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error updating customer');
      this.logger.debug(error, 'error updating customer');

      throw error;
    }
  }

  async updateCustomerAddress(
    customer: Customer,
    addressType: CustomerAddressType,
    address: CustomerAddress,
  ): Promise<Customer | null> {
    try {
      this.logger.info(
        { store: this.store, url: this.url },
        'updating customer address',
      );

      const route = UserRoute.ADDRESS.replace(
        '{customerId}',
        customer.reebeloId,
      ) as UserRoute;

      const updatedCustomer = await this.makeApiRequest<Customer>(
        'PUT',
        route,
        {},
        {
          addressType,
          ...address,
        },
      );

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          reebeloId: updatedCustomer?.reebeloId,
          shopifyId: updatedCustomer?.shopifyId,
        },
        'updated customer address',
      );

      return updatedCustomer;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error updating customer address');
      this.logger.debug(error, 'error updating customer address');

      return null;
    }
  }

  async updateCustomerSubscription(
    customer: Customer,
    channel: CustomerSubscriptionChannel,
    state: CustomerSubscriptionStatus,
  ): Promise<Customer> {
    try {
      this.logger.info(
        { store: this.store, url: this.url },
        'updating customer subscription',
      );

      const route = UserRoute.SUBSCRIPTION.replace(
        '{customerId}',
        customer.reebeloId,
      ) as UserRoute;

      const updatedCustomer = await this.makeApiRequest<Customer>(
        'PUT',
        route,
        {},
        {
          channel,
          state,
        },
      );

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          reebeloId: updatedCustomer?.reebeloId,
          shopifyId: updatedCustomer?.shopifyId,
        },
        'updated customer subscription',
      );

      if (!updatedCustomer) throw new Error('No response body');

      return updatedCustomer;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error updating customer subscription');
      this.logger.debug(error, 'error updating customer subscription');

      throw error;
    }
  }

  async createAlias(customer: Customer, alias: CustomerAlias) {
    try {
      this.logger.info(
        { store: this.store, url: this.url },
        'creating customer alias',
      );

      if (!customer || !customer.reebeloId) {
        this.logger.warn(
          { customer, alias },
          'Missing customer or customer reebelo id',
        );
      }

      const route = UserRoute.ALIAS.replace(
        '{customerId}',
        customer.reebeloId,
      ) as UserRoute;

      const createdAlias = await this.makeApiRequest<CustomerAlias>(
        'POST',
        route,
        {},
        {
          aliases: [alias],
        },
      );

      if (!createdAlias) throw new Error('No response body');

      this.logger.info(
        {
          store: this.store,
          url: this.url,
          reebeloId: customer.reebeloId,
          shopifyId: customer.shopifyId,
          aliasId: createdAlias.id,
        },
        'created customer alias',
      );

      return createdAlias;
    } catch (error: any) {
      const context = {
        store: this.store,
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error creating customer alias');
      this.logger.debug(error, 'error creating customer alias');

      throw error;
    }
  }

  async verifyCustomer(
    reebeloId: string,
    body: VerifyCustomer,
  ): Promise<{
    smartCaptureLink: string;
    workflowRunId: string;
  }> {
    const route = UserRoute.VERIFICATION.replace(
      '{customerId}',
      reebeloId,
    ) as UserRoute;

    this.logger.info(
      { body, route, store: this.store, url: this.url },
      'verifying customer',
    );
    const response = await this.makeApiRequest<{
      smartCaptureLink: string;
      workflowRunId: string;
    }>('POST', route, {}, body);

    if (!response) throw new Error('Error verifying customer');

    return response;
  }

  async getCustomerVerification(
    reebeloId: string,
    verificationId: string,
  ): Promise<{ data: CustomerVerification }> {
    const route = UserRoute.GET_VERIFICATION.replace(
      '{customerId}',
      reebeloId,
    ).replace('{verificationId}', verificationId) as UserRoute;

    const response = await this.makeApiRequest<{ data: CustomerVerification }>(
      'GET',
      route,
    );

    if (!response) throw new Error('Error getting customer verification');

    return response;
  }

  protected async makeApiRequest<T>(
    method: Method,
    route: UserRoute,
    headers: AxiosRequestConfig['headers'] = {},
    body = {},
    query = {},
  ) {
    let url;

    try {
      url = await this.getUrl(route);

      if (!url)
        throw new Error('error making customer api request - missing url');

      const payload: AxiosRequestConfig = {
        method,
        url,
        headers: {
          ...headers,
          ...(await this.getHeaders()),
        },
        data: body,
        params: query,
      };

      this.logger.debug({ payload }, 'making customer API request');

      const { data } = await axios(payload);

      this.logger.debug({ data }, 'response from customer API');

      return data as T;
    } catch (error: any) {
      const context = {
        store: this.store,
        method,
        route,
        url: axios.isAxiosError(error)
          ? error.config.url
          : await this.getUrl(route),
        status: axios.isAxiosError(error) ? error.response?.status : undefined,
        error: axios.isAxiosError(error) ? error.response?.data : error.message,
      };

      this.logger.error({ context }, 'error making customer API request');
      this.logger.debug(error, 'error making customer API request');

      throw error;
    }
  }

  private async getUrl(path: string): Promise<string | null> {
    const url =
      this.url ||
      (await this.ssm.getStoreValue(this.store, 'CUSTOMER_API_URL'));

    if (!this.url && url) this.url = url;

    return this.url + path;
  }

  private async getHeaders(): Promise<AxiosRequestConfig['headers']> {
    const apiKey =
      this.apiKey ||
      (await this.ssm.getStoreValue(this.store, 'CUSTOMER_API_KEY'));

    if (!this.apiKey && apiKey) this.apiKey = apiKey;

    return {
      'x-api-key': apiKey,
      'X-Store-Name': this.store,
    };
  }
}
